import userApplicationServices from "../../services/userApplicationServices"

// Create a global singleton for the cache
// This ensures the cache is shared across all imports of this module
const getGlobalCache = () => {
  // Use window object to store the cache globally
  if (typeof window !== "undefined") {
    // Initialize the global cache if it doesn't exist
    if (!window.__FIELDS_CONFIG_CACHE__) {
      window.__FIELDS_CONFIG_CACHE__ = {
        fieldVisibilityConfig: null,
        visibleFieldsCache: {},
      }

      // console.log('[DEBUG_LOG] Global cache initialized', window.__FIELDS_CONFIG_CACHE__);
    }

    return window.__FIELDS_CONFIG_CACHE__
  }

  // Fallback for non-browser environments
  return {
    fieldVisibilityConfig: null,
    visibleFieldsCache: {},
  }
}

// Counter to track API calls
let apiCallCounter = {
  total: 0,
  byKey: {},
}

// Track pending requests to avoid duplicate API calls
const pendingRequests = {}

// Get the global cache instance
const configCache = getGlobalCache()

/**
 * Fetches the field configuration from the API
 * @param {string} businessType - The business type (company_seeker, company_provider)
 * @param {string} category - The best describes category (corporation, financial, etc.)
 * @returns {Promise<Object>} - The field configuration
 */
export const fetchFieldsConfig = async (businessType, category) => {
  const cacheKey = `${businessType}_${category}`

  // Check if there's already a request in progress for this key
  if (pendingRequests[cacheKey]) {
    // console.log(`[DEBUG_LOG] Reusing pending request for ${cacheKey}`);
    return pendingRequests[cacheKey]
  }

  // Create a new promise for this request
  pendingRequests[cacheKey] = (async () => {
    try {
      // Increment API call counters
      apiCallCounter.total += 1
      apiCallCounter.byKey[cacheKey] = (apiCallCounter.byKey[cacheKey] || 0) + 1

      // Keep one log for API call tracking, but make it less verbose
      console.log(
        `[DEBUG_LOG] API call: ${cacheKey} (total: ${apiCallCounter.total})`,
      )

      // Get the latest global cache instance
      const currentCache = getGlobalCache()

      const response = await userApplicationServices.getFieldsConfig(
        businessType,
        category,
      )

      // Cache the field visibility config in the global cache
      currentCache.fieldVisibilityConfig = response.data.field_visibility_config

      // Cache the visible fields for this business type and category in the global cache
      if (businessType && category) {
        currentCache.visibleFieldsCache[cacheKey] = response.data.visible_fields
        console.log(
          `[DEBUG_LOG] Cache populated for ${cacheKey} with ${response.data.visible_fields.length} fields`,
        )
      }

      return response.data
    } catch (error) {
      console.error(
        `[DEBUG_LOG] Error fetching fields config for ${cacheKey}:`,
        error,
      )
      throw error
    } finally {
      // Clean up the pending request
      delete pendingRequests[cacheKey]
      // console.log(`[DEBUG_LOG] Removed pending request for ${cacheKey}`);
    }
  })()

  return pendingRequests[cacheKey]
}

/**
 * Gets the visible fields for a business type and category
 * If the configuration is not in the cache, it fetches it from the API
 * @param {string} businessType - The business type (company_seeker, company_provider)
 * @param {string} category - The best describes category (corporation, financial, etc.)
 * @returns {Promise<Array<string>>} - The visible fields
 */
export const getVisibleFields = async (businessType, category) => {
  // If we don't have a business type or category, return an empty array
  if (!businessType || !category) {
    return []
  }

  // Get the latest global cache instance
  const currentCache = getGlobalCache()

  // Check if we have the visible fields in the cache
  const cacheKey = `${businessType}_${category}`
  if (currentCache.visibleFieldsCache[cacheKey]) {
    // console.log(`[DEBUG_LOG] getVisibleFields: Cache HIT for ${cacheKey}`);
    return currentCache.visibleFieldsCache[cacheKey]
  }

  // Check if there's already a request in progress for this key
  if (pendingRequests[cacheKey]) {
    // console.log(`[DEBUG_LOG] getVisibleFields: Request already in progress for ${cacheKey}, waiting for it to complete`);
    const config = await pendingRequests[cacheKey]
    return config.visible_fields
  }

  // If not in cache and no pending request, fetch from API
  // console.log(`[DEBUG_LOG] getVisibleFields: Cache MISS for ${cacheKey}, no pending request, fetching from API`);
  const config = await fetchFieldsConfig(businessType, category)
  return config.visible_fields
}

/**
 * Checks if a field should be visible
 * This is a synchronous version that uses the cache
 * It returns false if the configuration is not in the cache
 * @param {string} fieldName - The name of the field
 * @param {string} businessType - The business type (company_seeker, company_provider)
 * @param {string} category - The best describes category (corporation, financial, etc.)
 * @returns {boolean} - Whether the field should be visible
 */
export const isFieldVisible = (fieldName, businessType, category) => {
  // If we don't have a business type or category, return false
  if (!businessType || !category) {
    return false
  }

  // Get the latest global cache instance
  const currentCache = getGlobalCache()

  // Check if we have the visible fields in the cache
  const cacheKey = `${businessType}_${category}`
  if (currentCache.visibleFieldsCache[cacheKey]) {
    const isVisible =
      currentCache.visibleFieldsCache[cacheKey].includes(fieldName)
    return isVisible
  }

  // If not in cache, we can't determine visibility synchronously
  // We'll return false and let the component handle fetching the configuration
  return false
}

/**
 * Gets the required fields for a business type and category
 * This is a synchronous version that uses the cache
 * It returns an empty array if the configuration is not in the cache
 * @param {string} businessType - The business type (company_seeker, company_provider)
 * @param {string} category - The best describes category (corporation, financial, etc.)
 * @param {Object} values - The form values
 * @returns {Array<string>} - The required fields
 */
export const getRequiredFields = (businessType, category, values = {}) => {
  // If we don't have a business type or category, return an empty array
  if (!businessType || !category) {
    return []
  }

  // Get the latest global cache instance
  const currentCache = getGlobalCache()

  // Check if we have the visible fields in the cache
  const cacheKey = `${businessType}_${category}`
  if (currentCache.visibleFieldsCache[cacheKey]) {
    const visibleFields = currentCache.visibleFieldsCache[cacheKey]

    // Filter out fields that shouldn't be required based on specific conditions
    const requiredFields = visibleFields.filter((field) => {
      // Customers/top 3 customers is optional across all flows
      if (field === "customers") {
        return false
      }
      // Special case for services field - only required if goodsServicesUploadOption is "text"
      if (field === "services" && values.goodsServicesUploadOption !== "text") {
        return false
      }

      // Special case for goodsServicesUpload field - only required if goodsServicesUploadOption is "upload"
      if (
        field === "goodsServicesUpload" &&
        values.goodsServicesUploadOption !== "upload"
      ) {
        return false
      }

      return true
    })

    return requiredFields
  }

  // If not in cache, we can't determine required fields synchronously
  return []
}

/**
 * Initializes the field configuration by fetching it from the API if not already cached
 * This should be called when the application starts or when the form is loaded
 * @param {string} businessType - The business type (company_seeker, company_provider)
 * @param {string} category - The best describes category (corporation, financial, etc.)
 * @returns {Promise<void>}
 */
export const initFieldsConfig = async (businessType, category) => {
  // If we don't have a business type or category, we can't fetch the config
  if (!businessType || !category) {
    return
  }

  // Get the latest global cache instance
  const currentCache = getGlobalCache()

  // Check if we already have the configuration for this business type and category
  const cacheKey = `${businessType}_${category}`

  if (currentCache.visibleFieldsCache[cacheKey]) {
    // Configuration is already cached, no need to fetch again
    return
  }

  // Check if there's already a request in progress for this key
  if (pendingRequests[cacheKey]) {
    // Wait for the pending request to complete
    await pendingRequests[cacheKey]
    return
  }

  // Not in cache and no pending request, fetch from API
  await fetchFieldsConfig(businessType, category)
}
